import { Injectable } from '@angular/core';

export interface AuthInteractionDraftState {
  selected_ids?: string[];
  page?: number;
  size?: number;
}

export interface PersistedAuthInteractionDraftBaseV2 {
  readonly collectionId: string;
  readonly revisionId: string;
  readonly targetId: string;
  readonly connectionId: string;
  readonly challengeId: string;
  readonly generation: number;
  readonly updatedAt: string;
}

export type PersistedAuthInteractionDraftV2 =
  | (PersistedAuthInteractionDraftBaseV2 & {
      /** Kinds without draft data: only the prompt identity is kept. */
      readonly kind: 'TWO_FA_CODE' | 'PUSH_APPROVAL';
    })
  | (PersistedAuthInteractionDraftBaseV2 & {
      readonly kind: 'SCOPE_SELECTION';
      readonly page: number;
      readonly selectedScopeIds: readonly string[];
    });

export interface AuthInteractionDraftIdentityV2
  extends Omit<PersistedAuthInteractionDraftBaseV2, 'updatedAt'> {
  readonly kind: 'TWO_FA_CODE' | 'PUSH_APPROVAL' | 'SCOPE_SELECTION';
}

@Injectable({
  providedIn: 'root',
})
export class AuthInteractionPersistenceService {
  private readonly retiredStorageKeyPrefix =
    'mzic_collection_auth_interaction_';
  private readonly v2StorageKeyPrefix = 'mzic_collection_auth_interaction_v2';

  constructor() {
    this.purgeRetiredDrafts();
  }

  saveDraft(draft: PersistedAuthInteractionDraftV2): void {
    assertDraft(draft);
    localStorage.setItem(this.getDraftStorageKey(draft), JSON.stringify(draft));
  }

  restoreDraft(
    expected: AuthInteractionDraftIdentityV2,
  ): PersistedAuthInteractionDraftV2 | null {
    const storageKey = this.getDraftStorageKey(expected);
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    try {
      const parsed: unknown = JSON.parse(raw);
      if (!isPersistedDraft(parsed) || !hasExactIdentity(parsed, expected)) {
        localStorage.removeItem(storageKey);
        return null;
      }
      return parsed;
    } catch {
      localStorage.removeItem(storageKey);
      return null;
    }
  }

  clearDraft(identity: AuthInteractionDraftIdentityV2): void {
    localStorage.removeItem(this.getDraftStorageKey(identity));
  }

  getDraftStorageKey(
    identity: Pick<
      PersistedAuthInteractionDraftBaseV2,
      'collectionId' | 'revisionId' | 'targetId' | 'connectionId'
    >,
  ): string {
    return [
      this.v2StorageKeyPrefix,
      identity.collectionId,
      identity.revisionId,
      identity.targetId,
      identity.connectionId,
    ].join('_');
  }

  clearCollection(collectionId: string): void {
    const prefix = `${this.v2StorageKeyPrefix}_${collectionId}_`;

    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  }

  private purgeRetiredDrafts(): void {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (
        key?.startsWith(this.retiredStorageKeyPrefix) &&
        !key.startsWith(this.v2StorageKeyPrefix)
      ) {
        localStorage.removeItem(key);
      }
    }
  }
}

const BASE_DRAFT_KEYS = [
  'collectionId',
  'revisionId',
  'targetId',
  'connectionId',
  'challengeId',
  'generation',
  'kind',
  'updatedAt',
] as const;

function assertDraft(draft: PersistedAuthInteractionDraftV2): void {
  if (!isPersistedDraft(draft)) {
    throw new TypeError('Auth interaction draft V2 is invalid.');
  }
}

function isPersistedDraft(
  value: unknown,
): value is PersistedAuthInteractionDraftV2 {
  if (!isRecord(value)) return false;
  const allowedKeys = new Set<string>(BASE_DRAFT_KEYS);
  if (value['kind'] === 'SCOPE_SELECTION') {
    allowedKeys.add('page');
    allowedKeys.add('selectedScopeIds');
  }
  if (Object.keys(value).some((key) => !allowedKeys.has(key))) return false;

  if (
    !isNonEmptyString(value['collectionId']) ||
    !isNonEmptyString(value['revisionId']) ||
    !isNonEmptyString(value['targetId']) ||
    !isNonEmptyString(value['connectionId']) ||
    !isNonEmptyString(value['challengeId']) ||
    !Number.isInteger(value['generation']) ||
    (value['generation'] as number) < 1 ||
    !isNonEmptyString(value['updatedAt'])
  ) {
    return false;
  }

  if (value['kind'] === 'TWO_FA_CODE' || value['kind'] === 'PUSH_APPROVAL') {
    return Object.keys(value).length === BASE_DRAFT_KEYS.length;
  }
  return (
    value['kind'] === 'SCOPE_SELECTION' &&
    Number.isInteger(value['page']) &&
    (value['page'] as number) >= 1 &&
    Array.isArray(value['selectedScopeIds']) &&
    value['selectedScopeIds'].every(isNonEmptyString)
  );
}

function hasExactIdentity(
  draft: PersistedAuthInteractionDraftV2,
  expected: AuthInteractionDraftIdentityV2,
): boolean {
  return (
    draft.collectionId === expected.collectionId &&
    draft.revisionId === expected.revisionId &&
    draft.targetId === expected.targetId &&
    draft.connectionId === expected.connectionId &&
    draft.challengeId === expected.challengeId &&
    draft.generation === expected.generation &&
    draft.kind === expected.kind
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
